import mixpanel from "mixpanel-browser";

import type { AnalyticsEvent, AnalyticsProviderInterface } from "../types";

const ALIAS_FLAG_PREFIX = "mp_alias_done_";
const IDENTIFY_FLAG_PREFIX = "mp_identify_done_";

export class MixpanelProvider implements AnalyticsProviderInterface {
  // Only used for logs
  readonly providerType = "Mixpanel";
  constructor(token: string, config?: { debug?: boolean }) {
    if (!token) {
      console.warn("Mixpanel token is empty or undefined");
      return;
    }

    mixpanel.init(token, {
      debug: config?.debug ?? false,
      ignore_dnt: true, // Add this to ignore "Do Not Track"
    });
    mixpanel.register({
      experience: "unified_webapp",
      channel: "web",
    });
  }

  sendEvent(eventName: string, properties?: AnalyticsEvent): void {
    mixpanel.track(eventName, properties);
  }

  // ---------- Identity ----------
  /**
   * Call this **only on first successful sign-up** for a user.
   * It links the current anonymous cookie id to your stable user id.
   */
  linkAnonymousToUser(userId: string): void {
    try {
      const key = `${ALIAS_FLAG_PREFIX}${userId}`;
      if (typeof window !== "undefined" && localStorage.getItem(key)) {
        return; // already aliased once for this user
      }
      const anonId = mixpanel.get_distinct_id();
      if (anonId && anonId !== userId) {
        mixpanel.alias(userId, anonId);
      }
      if (typeof window !== "undefined") {
        localStorage.setItem(key, "1");
      }
    } catch {
      // non-fatal
    }
  }

  /**
   * Use on every authenticated session (signup or login) AFTER linkAnonymousToUser (if signup).
   */
  identify(userId: string, traits?: Record<string, any>): void {
    try {
      const key = `${IDENTIFY_FLAG_PREFIX}${userId}`;
      if (typeof window !== "undefined" && localStorage.getItem(key)) {
        return; // already identified once for this user
      }
      mixpanel.identify(userId);
      if (traits && Object.keys(traits).length) {
        // Attach as super props for future events
        mixpanel.register(traits);
        // And write to the People (profile) object
        // Include $email if available so profiles are searchable in Mixpanel
        mixpanel.people?.set(traits);
        if (traits.$account_id) {
          mixpanel.set_group("account_id", traits.$account_id);
        }
      }
      if (typeof window !== "undefined") {
        localStorage.setItem(key, "1");
      }
    } catch {
      // non-fatal
    }
  }

  /**
   * Update the user's profile
   */
  updateUserProfile(traits?: Record<string, any>): void {
    if (traits && Object.keys(traits).length) {
      mixpanel.people?.set(traits);
    }
  }

  /**
   * Reset identity on logout (clears distinct_id, super props, groups, etc.).
   */
  reset(): void {
    try {
      if (typeof window !== "undefined") {
        // Remove all items with the identify flag prefix
        const keysToRemove = Array.from(
          { length: localStorage.length },
          (_, i) => localStorage.key(i),
        ).filter(
          (key): key is string => !!key && key.startsWith(IDENTIFY_FLAG_PREFIX),
        );
        keysToRemove.forEach((key) => localStorage.removeItem(key));
      }
      mixpanel.reset();
    } catch {
      // non-fatal
    }
  }

  // Optional helpers
  getDistinctId(): string {
    return mixpanel.get_distinct_id();
  }

  setSuperProps(props: Record<string, any>): void {
    mixpanel.register(props);
  }
}
